/**
 * Admin usage dashboard.
 *
 * Security note: this screen is only *surfaced* to users the server marks as
 * admins (`/me`.is_admin). The data itself is gated server-side by a step-up
 * secret (X-Admin-Token). The token is collected here, held in memory +
 * sessionStorage only (never localStorage), and cleared on "Lock".
 *
 * Tabs:
 *   - Overview : headline counters + per-day usage + top languages
 *   - Usage    : per-day / per-key "API call" rows across all users
 *   - Sessions : recent translation sessions; open one to read the transcript
 *                and hear the captured + synthesized audio for each turn.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../api";
import { ApiError } from "../api";
import { Btn, Field, IconBtn } from "../ui";
import { Icon } from "../icons";

type Tab = "overview" | "usage" | "sessions";

export function AdminScreen() {
  const [unlocked, setUnlocked] = useState(false);
  const [overview, setOverview] = useState<api.AdminOverview | null>(null);

  // Probe access on mount if a token is already cached this session.
  useEffect(() => {
    if (!api.hasAdminToken()) return;
    api.adminOverview()
      .then((o) => { setOverview(o); setUnlocked(true); })
      .catch(() => { api.setAdminToken(null); });
  }, []);

  if (!unlocked) {
    return <Unlock onUnlocked={(o) => { setOverview(o); setUnlocked(true); }} />;
  }
  return <Dashboard initialOverview={overview} onLock={() => { api.setAdminToken(null); setUnlocked(false); setOverview(null); }} />;
}

// ───────── token gate ─────────

function Unlock({ onUnlocked }: { onUnlocked: (o: api.AdminOverview) => void }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!token.trim() || busy) return;
    setBusy(true); setErr(null);
    api.setAdminToken(token.trim());
    try {
      const o = await api.adminOverview();
      onUnlocked(o);
    } catch (e) {
      api.setAdminToken(null);
      const code = e instanceof ApiError ? e.status : 0;
      setErr(code === 404 || code === 401 ? "Access denied. Check the admin token." : "Could not reach the admin API.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "70vh", padding: 24 }}>
      <div style={{
        width: "100%", maxWidth: 400, background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: "var(--r-lg)", padding: "30px 28px", boxShadow: "var(--shadow-sm)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <Icon name="shield" size={22} color="var(--accent)" />
          <h1 className="display" style={{ fontSize: 21, fontWeight: 700, margin: 0, color: "var(--ink)" }}>Admin access</h1>
        </div>
        <p style={{ fontSize: 13.5, color: "var(--ink-3)", margin: "0 0 20px", lineHeight: 1.5 }}>
          Enter the admin step-up token to view product usage. The token is kept in this tab only.
        </p>
        <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <Field
            icon="lock" type="password" value={token} onChange={setToken}
            placeholder="X-Admin-Token" autoFocus mono
          />
          {err && <div style={{ color: "var(--danger, #d33)", fontSize: 13, marginTop: 12 }}>{err}</div>}
          <div style={{ marginTop: 18 }}>
            <Btn type="submit" full disabled={busy || !token.trim()} icon="shield">
              {busy ? "Checking…" : "Unlock dashboard"}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  );
}

// ───────── dashboard shell ─────────

function Dashboard({ initialOverview, onLock }: { initialOverview: api.AdminOverview | null; onLock: () => void }) {
  const [tab, setTab] = useState<Tab>("overview");
  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 26px 64px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icon name="shield" size={22} color="var(--accent)" />
          <h1 className="display" style={{ fontSize: 27, fontWeight: 700, color: "var(--ink)", margin: 0, letterSpacing: -0.6 }}>
            Usage dashboard
          </h1>
        </div>
        <Btn variant="outline" size="sm" icon="lock" onClick={onLock}>Lock</Btn>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 22, borderBottom: "1px solid var(--border)" }}>
        {(["overview", "usage", "sessions"] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: "none", border: "none", cursor: "pointer", padding: "10px 14px",
            fontSize: 14.5, fontWeight: 600, fontFamily: "var(--font-ui)", textTransform: "capitalize",
            color: tab === t ? "var(--ink)" : "var(--ink-3)",
            borderBottom: `2px solid ${tab === t ? "var(--accent)" : "transparent"}`, marginBottom: -1,
          }}>{t}</button>
        ))}
      </div>

      {tab === "overview" && <Overview initial={initialOverview} />}
      {tab === "usage" && <UsageTab />}
      {tab === "sessions" && <SessionsTab />}
    </div>
  );
}

// ───────── overview ─────────

function Overview({ initial }: { initial: api.AdminOverview | null }) {
  const [data, setData] = useState<api.AdminOverview | null>(initial);
  const [loading, setLoading] = useState(!initial);

  const reload = useCallback(() => {
    setLoading(true);
    api.adminOverview().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { if (!initial) reload(); }, [initial, reload]);

  if (loading && !data) return <Muted>Loading…</Muted>;
  if (!data) return <Muted>No data.</Muted>;

  const maxAudio = Math.max(1, ...data.usage_by_day.map((d) => d.audio_seconds));
  return (
    <div style={{ display: "grid", gap: 22 }}>
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}>
        <Stat label="Users" value={data.total_users} />
        <Stat label="Sessions" value={data.total_sessions} />
        <Stat label="Utterances" value={data.total_utterances} />
        <Stat label="Turns" value={data.total_turns} />
        <Stat label="Active now" value={data.active_sessions} accent />
        <Stat label="Sessions / 24h" value={data.sessions_last_24h} />
      </div>

      <Card title={`Usage · last ${data.usage_window_days} days`} right={<IconBtn name="refresh" onClick={reload} size={34} ic={16} title="Refresh" />}>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 16, fontSize: 13 }}>
          <Totals label="Audio" value={fmtDur(data.usage_totals.audio_seconds)} />
          <Totals label="Sessions" value={String(data.usage_totals.session_count)} />
          <Totals label="Chars in" value={fmtNum(data.usage_totals.chars_in)} />
          <Totals label="Chars out" value={fmtNum(data.usage_totals.chars_out)} />
          <Totals label="Est. cost" value={`$${data.usage_totals.cost_usd.toFixed(2)}`} />
        </div>
        {data.usage_by_day.length === 0 ? (
          <Muted>No usage recorded in this window.</Muted>
        ) : (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 120 }}>
            {data.usage_by_day.map((d) => (
              <div key={d.date} title={`${d.date}\n${fmtDur(d.audio_seconds)} · ${d.session_count} sessions`}
                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                <div style={{
                  width: "100%", maxWidth: 34, borderRadius: "4px 4px 0 0", background: "var(--accent)",
                  height: `${Math.round((d.audio_seconds / maxAudio) * 100)}%`, minHeight: 2,
                }} />
                <div className="mono" style={{ fontSize: 9.5, color: "var(--ink-faint)", whiteSpace: "nowrap" }}>
                  {d.date.slice(5)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
        <Card title="Top source languages"><LangBars rows={data.top_source_langs} /></Card>
        <Card title="Top target languages"><LangBars rows={data.top_target_langs} /></Card>
      </div>
    </div>
  );
}

function LangBars({ rows }: { rows: api.AdminLangCount[] }) {
  if (rows.length === 0) return <Muted>No turns yet.</Muted>;
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {rows.map((r) => (
        <div key={r.lang} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="mono" style={{ width: 64, fontSize: 12, color: "var(--ink-2)" }}>{r.lang}</div>
          <div style={{ flex: 1, background: "var(--surface-2)", borderRadius: 4, height: 16, overflow: "hidden" }}>
            <div style={{ width: `${Math.round((r.count / max) * 100)}%`, height: "100%", background: "var(--accent-soft)" }} />
          </div>
          <div className="mono" style={{ width: 44, textAlign: "right", fontSize: 12, color: "var(--ink-3)" }}>{r.count}</div>
        </div>
      ))}
    </div>
  );
}

// ───────── usage tab ─────────

function UsageTab() {
  const [rows, setRows] = useState<api.AdminUsageRow[] | null>(null);
  useEffect(() => { api.adminUsage(30).then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return <Muted>Loading…</Muted>;
  if (rows.length === 0) return <Muted>No usage rows in the last 30 days.</Muted>;
  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: "var(--r-lg)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--ink-3)", background: "var(--surface-2)" }}>
            {["Date", "User", "Key", "Audio", "Sessions", "Chars in", "Chars out", "Cost"].map((h) => (
              <th key={h} style={{ padding: "10px 12px", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.user_id}-${r.usage_date}-${i}`} style={{ borderTop: "1px solid var(--border)" }}>
              <td className="mono" style={td}>{r.usage_date}</td>
              <td style={td}>{r.user_email}</td>
              <td style={td}>{r.api_key_label}</td>
              <td className="mono" style={td}>{fmtDur(r.audio_seconds)}</td>
              <td className="mono" style={td}>{r.session_count}</td>
              <td className="mono" style={td}>{fmtNum(r.chars_in)}</td>
              <td className="mono" style={td}>{fmtNum(r.chars_out)}</td>
              <td className="mono" style={td}>${r.cost_usd.toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ───────── sessions tab ─────────

function SessionsTab() {
  const [rows, setRows] = useState<api.AdminSessionRow[] | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  useEffect(() => { api.adminSessions(100).then(setRows).catch(() => setRows([])); }, []);

  if (openId != null) return <SessionDetail sid={openId} onBack={() => setOpenId(null)} />;
  if (!rows) return <Muted>Loading…</Muted>;
  if (rows.length === 0) return <Muted>No sessions yet.</Muted>;
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {rows.map((s) => (
        <button key={s.id} onClick={() => setOpenId(s.id)} style={{
          textAlign: "left", cursor: "pointer", border: "1px solid var(--border)", background: "var(--surface)",
          borderRadius: "var(--r-md)", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: 14.5 }}>{s.user_email}</div>
            <div className="mono" style={{ fontSize: 11.5, color: "var(--ink-faint)" }}>
              #{s.id} · {new Date(s.started_at).toLocaleString()} {s.ended_at ? "" : "· live"}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
            <span className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>{s.turn_count} turns</span>
            <Icon name="chevronRight" size={16} color="var(--ink-3)" />
          </div>
        </button>
      ))}
    </div>
  );
}

function SessionDetail({ sid, onBack }: { sid: number; onBack: () => void }) {
  const [data, setData] = useState<api.AdminSessionDetail | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.adminSessionDetail(sid).then(setData).catch(() => setErr(true)); }, [sid]);

  return (
    <div>
      <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "var(--ink-2)", fontSize: 14, fontWeight: 600, padding: "4px 0", marginBottom: 14 }}>
        <Icon name="chevronLeft" size={18} /> Back to sessions
      </button>
      {err ? <Muted>Could not load session.</Muted> : !data ? <Muted>Loading…</Muted> : (
        <>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 18, color: "var(--ink)" }}>{data.user_email}</div>
            <div className="mono" style={{ fontSize: 12, color: "var(--ink-faint)" }}>
              session #{data.id} · {new Date(data.started_at).toLocaleString()}
            </div>
          </div>
          {data.utterances.length === 0 ? <Muted>No utterances captured.</Muted> : (
            <div style={{ display: "grid", gap: 14 }}>
              {data.utterances.map((u) => (
                <div key={u.id} style={{ border: "1px solid var(--border)", borderRadius: "var(--r-md)", padding: "14px 16px", background: "var(--surface)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <div className="mono" style={{ fontSize: 11.5, color: "var(--ink-faint)" }}>utterance {u.utt_id}</div>
                    {u.has_full_audio && <AudioBtn label="Full audio" load={() => api.adminUtteranceAudioUrl(u.id)} />}
                  </div>
                  <div style={{ display: "grid", gap: 10 }}>
                    {u.turns.map((t) => (
                      <div key={t.id} style={{ display: "grid", gap: 4, paddingLeft: 10, borderLeft: `2px solid ${t.label === "OWNER" ? "var(--accent)" : "var(--border-strong)"}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span className="mono" style={{ fontSize: 10.5, fontWeight: 700, color: t.label === "OWNER" ? "var(--accent)" : "var(--ink-3)" }}>{t.label}</span>
                          <span className="mono" style={{ fontSize: 10.5, color: "var(--ink-faint)" }}>{t.src_lang ?? "?"} → {t.tgt_lang ?? "?"}</span>
                          {t.has_turn_audio && <AudioBtn label="Heard" load={() => api.adminTurnAudioUrl(t.id, "src")} />}
                          {t.has_tts_audio && <AudioBtn label="Spoken" load={() => api.adminTurnAudioUrl(t.id, "tts")} />}
                        </div>
                        {t.src_text && <div style={{ fontSize: 14, color: "var(--ink)" }}>{t.src_text}</div>}
                        {t.tgt_text && <div style={{ fontSize: 13.5, color: "var(--ink-3)", fontStyle: "italic" }}>{t.tgt_text}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ───────── audio button ─────────

function AudioBtn({ label, load }: { label: string; load: () => Promise<string> }) {
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => () => {
    audioRef.current?.pause();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const toggle = async () => {
    if (playing) { audioRef.current?.pause(); return; }
    if (busy) return;
    setBusy(true);
    try {
      if (!urlRef.current) urlRef.current = await load();
      if (!audioRef.current) {
        const a = new Audio(urlRef.current);
        a.onended = () => setPlaying(false);
        a.onpause = () => setPlaying(false);
        a.onplay = () => setPlaying(true);
        audioRef.current = a;
      }
      await audioRef.current.play();
    } catch { /* ignore playback errors */ } finally {
      setBusy(false);
    }
  };

  return (
    <button onClick={toggle} style={{
      display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer",
      border: "1px solid var(--border)", background: "var(--surface-2)", borderRadius: "var(--r-pill)",
      padding: "3px 9px 3px 7px", fontSize: 11.5, fontWeight: 600, color: "var(--ink-2)",
    }}>
      <Icon name={busy ? "clock" : playing ? "pause" : "play"} size={12} />
      {label}
    </button>
  );
}

// ───────── small presentational helpers ─────────

const td: React.CSSProperties = { padding: "9px 12px", whiteSpace: "nowrap" };

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md)", padding: "16px 18px" }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent ? "var(--accent)" : "var(--ink)" }}>{fmtNum(value)}</div>
      <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>{label}</div>
    </div>
  );
}

function Totals({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="mono" style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{value}</div>
      <div style={{ fontSize: 11.5, color: "var(--ink-faint)" }}>{label}</div>
    </div>
  );
}

function Card({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-lg)", padding: "18px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div className="display" style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ color: "var(--ink-3)", fontSize: 14, padding: "8px 0" }}>{children}</div>;
}

function fmtNum(n: number): string { return n.toLocaleString(); }
function fmtDur(secs: number): string {
  secs = Math.round(secs);
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60), s = secs % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
